import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { MathJaxRenderer } from '../../components/math-renderers';
import { MyTestDetail, answerLabel, myTestApi, showTime } from '../../utils/myTestApi';
import { QuestionBody, optionsOf } from './QuestionBody';

/**
 * One test, after it is done: submitted, and the result when the teacher's
 * setting allows (`results_visible`, decided by the API). Until then the
 * page says when, or that the teacher will release them -- and shows no
 * score, because the API sends none.
 */
const MyTestResult: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const just = (location.state as any) || {};
  const [t, setT] = useState<MyTestDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openQ, setOpenQ] = useState<number | null>(null);

  useEffect(() => {
    myTestApi
      .get(Number(id))
      .then(r => setT(r.result))
      .catch(e => setError(e.message));
  }, [id]);

  if (error) {
    return (
      <div className="container py-5" style={{ maxWidth: 720 }}>
        <div className="alert alert-warning">{error}</div>
        <button className="btn btn-primary" onClick={() => navigate('/my-tests')}>Back to my tests</button>
      </div>
    );
  }
  if (!t) {
    return (
      <div className="container py-5 text-center">
        <div className="spinner-border text-primary" role="status" />
      </div>
    );
  }

  const done = t.state === 'submitted' || t.state === 'auto_submitted';
  const r = t.result;

  return (
    <div className="container py-4" style={{ maxWidth: 900 }}>
      <button className="btn btn-link px-0 mb-2" onClick={() => navigate('/my-tests')}>
        ← My tests
      </button>
      <h3 className="mb-1">{t.title}</h3>
      <div className="text-muted mb-4">
        {t.submitted_at ? `Submitted ${showTime(t.submitted_at)}` : `${showTime(t.scheduled_start)} – ${showTime(t.scheduled_end)}`}
      </div>

      {just.justSubmitted && (
        <div className={`alert ${just.auto ? 'alert-warning' : 'alert-success'}`}>
          {just.auto ? 'Time was up, so your test was submitted with the answers you gave.' : 'Your test has been submitted.'}
        </div>
      )}

      {t.in_progress && (
        <div className="alert alert-info d-flex justify-content-between align-items-center">
          <span>You have this test open.</span>
          <button className="btn btn-primary" onClick={() => navigate(`/my-tests/${t.assignment_id}/take`)}>Continue</button>
        </div>
      )}
      {!t.in_progress && t.can_start && (
        <div className="alert alert-light border d-flex justify-content-between align-items-center">
          <span>
            You can take this test again ({t.attempts_used} of {t.max_attempts} used). Your best score counts.
          </span>
          <button className="btn btn-primary" onClick={() => navigate(`/my-tests/${t.assignment_id}/take`)}>Try again</button>
        </div>
      )}
      {t.state === 'missed' && <div className="alert alert-warning">You did not take this test before it closed.</div>}
      {t.state === 'excused' && <div className="alert alert-light border">Your teacher excused you from this test.</div>}

      {done && !t.results_visible && (
        <div className="card shadow-sm">
          <div className="card-body">
            <h5>Results</h5>
            <p className="mb-0 text-muted">
              {t.show_results_mode === 'after_window' && t.results_available_at
                ? `Your result will be shown here after the test closes for everyone, on ${showTime(t.results_available_at)}.`
                : t.show_results_mode === 'manual'
                ? 'Your teacher will release the results. Check back later.'
                : 'Your teacher has chosen not to share results for this test.'}
            </p>
          </div>
        </div>
      )}

      {r && (
        <>
          <div className="card shadow-sm mb-4">
            <div className="card-body d-flex flex-wrap gap-4 align-items-center">
              <div>
                <div className="small text-muted">Score</div>
                <div className="display-6">
                  {r.score} <span className="fs-5 text-muted">/ {r.totals.total_marks}</span>
                </div>
              </div>
              <div>
                <div className="small text-muted">Percentage</div>
                <div className="fs-3">{r.percentage}%</div>
              </div>
              <div className="small">
                <div className="text-success">{r.totals.correct} right</div>
                <div className="text-danger">{r.totals.incorrect} wrong</div>
                <div className="text-muted">{r.totals.total_questions - r.totals.answered} left blank</div>
              </div>
              {t.max_attempts > 1 && <div className="small text-muted">Your best attempt is shown.</div>}
            </div>
          </div>

          <h5 className="mb-3">Question by question</h5>
          {r.questions.map((q, i) => {
            const blank = !q.user_answer;
            const open = openQ === i;
            return (
              <div key={q.id} className="card mb-2">
                <button
                  type="button"
                  className="card-header bg-white d-flex flex-wrap gap-2 align-items-center text-start border-0 w-100"
                  onClick={() => setOpenQ(open ? null : i)}
                  aria-expanded={open}
                >
                  <strong>Q{i + 1}</strong>
                  <span className={`badge ${blank ? 'bg-secondary' : q.is_correct ? 'bg-success' : 'bg-danger'}`}>
                    {blank ? 'Blank' : q.is_correct ? 'Right' : 'Wrong'}
                  </span>
                  <span className="small">
                    Your answer <strong>{answerLabel(q.user_answer, q.question_type)}</strong> · Correct{' '}
                    <strong>{answerLabel(q.correct_answer, q.question_type)}</strong>
                  </span>
                  <span className="ms-auto small text-muted">
                    {q.marks_obtained} / {q.marks}
                  </span>
                </button>
                {open && (
                  <div className="card-body">
                    <QuestionBody content={q.content} />
                    {optionsOf(q.content).some(([, , text]) => text) && (
                      <ul className="list-unstyled">
                        {optionsOf(q.content).map(([value, letter, text]) => (
                          <li
                            key={value}
                            className={value === q.correct_answer ? 'text-success fw-bold' : value === q.user_answer ? 'text-danger' : ''}
                          >
                            {letter}. {text ? <MathJaxRenderer>{text}</MathJaxRenderer> : null}
                          </li>
                        ))}
                      </ul>
                    )}
                    {q.content?.solution && (
                      <div className="mt-3 p-3 bg-light rounded">
                        <div className="small text-muted mb-1">Solution</div>
                        {/^https?:\/\//.test(q.content.solution.trim()) ? (
                          <img src={q.content.solution.trim()} alt="Solution" className="img-fluid" />
                        ) : (
                          <MathJaxRenderer>{q.content.solution}</MathJaxRenderer>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}
    </div>
  );
};

export default MyTestResult;
